import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';


/**
 * FrankOs search scope.
 * Predictive search and the full /search page now use the same public FrankOs
 * search service so both surfaces have exactly the same strict five-field scope:
 * title, product type, vendor/brand, SKU/reference and barcode/EAN.
 */
const FRANKOS_PREDICTIVE_API_PATH = '/apps/frankos/search/products';
const FRANKOS_PREDICTIVE_LIMIT = 8;

/**
 * Build the customer-facing search-results URL.
 * The raw query remains readable in q; the FrankOs search section uses this term
 * for the public App Proxy search service instead of Shopify's broad full-search engine.
 * @param {string} searchTerm
 * @returns {URL}
 */
function buildFrankOsSearchUrl(searchTerm) {
  const cleanSearchTerm = searchTerm.trim();
  const searchUrl = new URL(Theme.routes.search_url, location.origin);

  searchUrl.searchParams.set('q', cleanSearchTerm);
  searchUrl.searchParams.set('type', 'product');

  return searchUrl;
}


/**
 * Escape text before inserting it into predictive-search markup.
 * @param {unknown} value
 * @returns {string}
 */
function escapeFrankOsHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Build a responsive Shopify CDN image URL.
 * @param {string | null | undefined} value
 * @param {number} width
 * @returns {string}
 */
function frankOsPredictiveImageUrl(value, width) {
  if (!value) return '';

  try {
    const url = new URL(value, location.origin);
    if (url.protocol !== 'https:') return '';
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return '';
  }
}

/**
 * Format a product-card price in the same Dutch EUR presentation used by FrankOs.
 * @param {string | number | null | undefined} value
 * @returns {string}
 */
function formatFrankOsPredictivePrice(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';

  return `${new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount)} EUR`;
}

/**
 * Render one product using Horizon's existing predictive-search/resource-card classes.
 * @param {object} product
 * @returns {string}
 */
function renderFrankOsPredictiveProduct(product) {
  const handle = String(product?.handle || '');
  const title = String(product?.title || '');
  const productUrl = `/products/${encodeURIComponent(handle)}`;
  const image = product?.image?.url ? product.image : null;
  const price = formatFrankOsPredictivePrice(product?.price_min);

  let imageMarkup = '';

  if (image) {
    const src = frankOsPredictiveImageUrl(image.url, 352);
    const srcset = [240, 352, 500]
      .map((width) => `${frankOsPredictiveImageUrl(image.url, width)} ${width}w`)
      .join(', ');

    imageMarkup =
      `<img class="resource-card__image"` +
      ` src="${escapeFrankOsHtml(src)}"` +
      ` srcset="${escapeFrankOsHtml(srcset)}"` +
      ` sizes="auto"` +
      ` alt="${escapeFrankOsHtml(image.alt || title)}"` +
      ` loading="lazy">`;
  } else {
    imageMarkup =
      `<div aria-hidden="true" class="resource-card__image-placeholder">` +
      `${escapeFrankOsHtml(title.slice(0, 60))}</div>`;
  }

  return (
    `<li class="predictive-search-results__card predictive-search-results__card--product" ref="resultsItems[]">` +
      `<div class="resource-card" data-resource-type="product">` +
        `<a class="resource-card__link" href="${escapeFrankOsHtml(productUrl)}">` +
          `<span class="visually-hidden">${escapeFrankOsHtml(title)}</span>` +
        `</a>` +
        `<div class="resource-card__media" style="--resource-card-aspect-ratio: 4 / 5;">` +
          imageMarkup +
        `</div>` +
        `<div class="resource-card__content">` +
          `<p class="resource-card__title paragraph">${escapeFrankOsHtml(title)}</p>` +
          `<div class="price-container">` +
            `<div class="price__regular">` +
              `<span class="price">${escapeFrankOsHtml(price)}</span>` +
            `</div>` +
          `</div>` +
        `</div>` +
      `</div>` +
    `</li>`
  );
}

/**
 * Render the complete predictive-search result block from the FrankOs search API.
 * @param {string} searchTerm
 * @param {object[]} items
 * @param {number} total
 * @returns {string}
 */
function renderFrankOsPredictiveMarkup(searchTerm, items, total) {
  const safeTerm = escapeFrankOsHtml(searchTerm);
  const products = Array.isArray(items) ? items.slice(0, FRANKOS_PREDICTIVE_LIMIT) : [];

  if (!products.length) {
    return (
      `<div id="predictive-search-results" class="predictive-search-dropdown" role="listbox"` +
        ` aria-label="Zoekresultaten" data-transition-container>` +
        `<div class="predictive-search-results__inner" data-search-results>` +
          `<div class="visually-hidden" role="status">Geen zoekresultaten voor ${safeTerm}</div>` +
          `<p class="predictive-search-results__no-results">Geen resultaten voor “${safeTerm}”</p>` +
        `</div>` +
      `</div>`
    );
  }

  const singleResult =
    total === 1 && products.length === 1
      ? `<div data-single-result-url="/products/${escapeFrankOsHtml(encodeURIComponent(String(products[0]?.handle || '')))}"></div>`
      : '';

  return (
    `<div id="predictive-search-results" class="predictive-search-dropdown" role="listbox"` +
      ` aria-label="Zoekresultaten" data-transition-container>` +
      `<div class="predictive-search-results__inner" data-search-results>` +
        `<div class="visually-hidden" role="status">` +
          `${Number(total || products.length)} zoekresultaten voor ${safeTerm}` +
        `</div>` +
        `<div id="predictive-search-products" class="predictive-search-results__products">` +
          `<h4 class="predictive-search-results__title">Producten</h4>` +
          `<ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled">` +
            products.map(renderFrankOsPredictiveProduct).join('') +
          `</ul>` +
        `</div>` +
        singleResult +
      `</div>` +
    `</div>`
  );
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    const searchForm = this.querySelector('form[role="search"]');
    if (searchForm instanceof HTMLFormElement) {
      searchForm.addEventListener('submit', this.#handleSearchSubmit, { signal });
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Sends every full search through the strict FrankOs field scope.
   * Predictive result links themselves remain directly clickable.
   * @param {SubmitEvent} event
   */
  #handleSearchSubmit = (event) => {
    const searchTerm = this.refs.searchInput.value.trim();
    if (!searchTerm.length) return;

    event.preventDefault();
    window.location.href = buildFrankOsSearchUrl(searchTerm).toString();
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          window.location.href = buildFrankOsSearchUrl(this.refs.searchInput.value).toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    const url = new URL(FRANKOS_PREDICTIVE_API_PATH, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('page', '1');
    url.searchParams.set('per_page', String(FRANKOS_PREDICTIVE_LIMIT));
    url.searchParams.set('sort', 'relevance');

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    fetch(url, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`FrankOs predictive search gaf HTTP ${response.status}.`);
        }
        return response.json();
      })
      .then((payload) => {
        if (abortController.signal.aborted) return;
        if (!payload || payload.success !== true || !payload.data) {
          throw new Error('Ongeldige reactie van FrankOs predictive search.');
        }

        const items = Array.isArray(payload.data.items) ? payload.data.items : [];
        const total = Number(payload.data.pagination?.total || items.length);

        predictiveSearchResults.innerHTML = renderFrankOsPredictiveMarkup(searchTerm, items, total);
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        console.error('[FrankOs Predictive Search]', error);
        predictiveSearchResults.innerHTML =
          '<div id="predictive-search-results" class="predictive-search-dropdown" role="listbox"' +
          ' aria-label="Zoekresultaten" data-transition-container>' +
          '<div class="predictive-search-results__inner" data-search-results>' +
          '<p class="predictive-search-results__no-results">Zoeken is tijdelijk niet beschikbaar.</p>' +
          '</div></div>';
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
